import React, { useState } from "react";
import Grid from "@material-ui/core/Grid";
import { Typography } from "@material-ui/core";
import FormControlLabel from "@material-ui/core/FormControlLabel";
import Radio from "@material-ui/core/Radio";
import RadioGroup from "@material-ui/core/RadioGroup";
import FormControl from "@material-ui/core/FormControl";
import Divider from "@material-ui/core/Divider";
import { Icon, SecondaryButton } from "@prestosports/commons-ui";
import ArrowsHorizontal32 from "@carbon/icons-react/lib/arrows--horizontal/32";
import { SportCodes } from "@prestosports/prestostats-core/Common";
import { useTeamsContext } from "../../../state/teams/context";
import ballPositionStyles from "../styles/BallPosition.styles";
import { useGameContextContext } from "../../../state/game-context/context";
import { useEventContext } from "../../../state/events/context";
import EventSettingsPreview from "./EventSettingsPreview";
import OffenseTeam from "./OffenseTeam";
import { getTossQuestion } from "./utils/OvertimeUtils";

const GameConfig = ({
	settings,
	onChange = () => {},
	title,
	subtitle,
	hideSwapTeams
}) => {
	const { state: gameContextContextState } = useGameContextContext();
	const overtimePeriod = gameContextContextState.overtimePeriod();
	const otKickoffs = gameContextContextState.otKickoffs();

	const { state: eventContextState } = useEventContext();
	const { sportId } = eventContextState.currentEvent() || {};
	const kickoffEnabled = eventContextState.kickoffEnabled();

	const ballPositionClasses = ballPositionStyles();

	const [startSettings, setStartSettings] = useState(settings);
	const { tossWinner, offenseTeam, swapTeams } = startSettings || {};

	const { state } = useTeamsContext();
	const homeTeam = state.homeTeam();
	const awayTeam = state.awayTeam();
	const isFlagFootball = sportId === SportCodes.FlagFootball;
	// A flag game with kickoffs enabled starts the same way a tackle game
	// does, so it asks which team is receiving rather than which is on
	// offense. Overtime asks the same only when OT Kickoffs follows its toss
	// with a kickoff (PSTATS-1268).
	const startsWithoutKickoff = isFlagFootball && !kickoffEnabled;
	const { asksReceivingTeam, storesKickingTeam } = getTossQuestion({
		overtimePeriod,
		otKickoffs,
		startsWithoutKickoff
	});

	const setSetting = (setting) => {
		const newValues = { ...startSettings, ...setting };
		setStartSettings(newValues);
		onChange(newValues);
	};

	const offenseTeamTitle = `2. ${
		asksReceivingTeam
			? "Which team is receiving the kick off?"
			: "Which team is starting on offense?"
	}`;

	const controlId = asksReceivingTeam ? "receiving" : "offense";
	const offenseTeamControls = [
		{
			label: awayTeam.teamName,
			id: `${controlId}-away`,
			value: storesKickingTeam ? homeTeam.id : awayTeam.id
		},
		{
			label: homeTeam.teamName,
			id: `${controlId}-home`,
			value: storesKickingTeam ? awayTeam.id : homeTeam.id
		}
	];

	const onOffenseTeamSelect = (event) => {
		setSetting({
			offenseTeam: event.target.value === homeTeam.id ? homeTeam : awayTeam
		});
	};

	return (
		<Grid container direction="column" spacing={4}>
			<Grid item>
				<Typography variant="h3">{title}</Typography>
				<Typography variant="body1">{subtitle}</Typography>
			</Grid>
			<Grid item>
				<Typography variant="h5">1. Which team won the coin toss?</Typography>
				<FormControl component="fieldset">
					<RadioGroup
						row
						aria-label="coin toss winner"
						name="coinTossWinner"
						value={tossWinner ? tossWinner.id : ""}
						onChange={(event) => {
							setSetting({
								tossWinner: event.target.value === homeTeam.id ? homeTeam : awayTeam
							});
						}}
					>
						<FormControlLabel
							id="coin-toss-away"
							value={awayTeam.id}
							control={<Radio color="primary" />}
							label={awayTeam.teamName}
						/>

						<FormControlLabel
							id="coin-toss-home"
							value={homeTeam.id}
							control={<Radio color="primary" />}
							label={homeTeam.teamName}
						/>
					</RadioGroup>
				</FormControl>
			</Grid>
			<Divider />
			<Grid item>
				<OffenseTeam
					title={offenseTeamTitle}
					offenseTeamId={offenseTeam?.id}
					controls={offenseTeamControls}
					onChange={onOffenseTeamSelect}
				/>
			</Grid>
			{!hideSwapTeams && (
				<>
					<Divider />
					<Grid item>
						<Typography variant="h5">3. Is the field layout correct?</Typography>
						<Grid item container justifyContent="space-between">
							{swapTeams && (
								<>
									<Grid item>{homeTeam.teamName}</Grid>
									<Grid item>{awayTeam.teamName}</Grid>
								</>
							)}
							{!swapTeams && (
								<>
									<Grid item>{awayTeam.teamName}</Grid>
									<Grid item>{homeTeam.teamName}</Grid>
								</>
							)}
						</Grid>
						<Grid item>
							<div className={ballPositionClasses.fieldBar} />
						</Grid>
						<Grid item container justifyContent="center">
							<SecondaryButton
								id="swap-teams-button"
								size="small"
								onClick={() => setSetting({ swapTeams: !swapTeams })}
								label="Change Field Direction"
								startIcon={
									<Icon>
										<ArrowsHorizontal32 />
									</Icon>
								}
							/>
						</Grid>
					</Grid>
				</>
			)}
			<EventSettingsPreview />
		</Grid>
	);
};

export default GameConfig;
